import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { XCircle } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Alert, AlertDescription } from '@/shared/components/ui/alert';

export default function InvoiceSubscriptionErrorPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [canClose, setCanClose] = useState(true);
  const errorMessage = searchParams.get('message') || 'Payment failed or was canceled';

  useEffect(() => {
    console.log('[InvoiceSubscriptionError] Page loaded with error:', errorMessage);

    // Check if opened from web app (has window.opener)
    if (window.opener && !window.opener.closed) {
      try {
        console.log('[InvoiceSubscriptionError] Sending error message to parent window');

        // Send message to parent
        window.opener.postMessage(
          { type: 'STRIPE_CHECKOUT_ERROR', error: errorMessage },
          window.location.origin
        );

        // Wait 1.5 seconds before trying to close (let user see the error)
        setTimeout(() => {
          console.log('[InvoiceSubscriptionError] Attempting to close tab...');

          // Try to close
          window.close();

          // Try again after 100ms
          setTimeout(() => window.close(), 100);

          // If we're still here after 1 second, window.close() failed
          setTimeout(() => {
            if (!window.closed) {
              setCanClose(false);
              console.log('[InvoiceSubscriptionError] Auto-close failed, showing manual close button');
            }
          }, 1000);
        }, 1500);
      } catch (error) {
        console.error('[InvoiceSubscriptionError] Failed to send message to parent:', error);
        setCanClose(false);
      }
    }
    // Fallback: Direct navigation (no parent window)
    else {
      console.log('[InvoiceSubscriptionError] No parent window - user navigated directly');
      setCanClose(false);
    }
  }, [errorMessage]);

  const handleRetry = () => {
    if (window.opener) {
      window.close();
      // Parent window should still be on the invoices page where they can retry
    } else {
      navigate('/invoices');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-orange-50 to-white flex items-center justify-center p-4">
      <div className="max-w-md w-full text-center space-y-6">
        {/* Error Icon */}
        <div className="flex justify-center">
          <div className="w-24 h-24 bg-gradient-to-br from-red-400 to-orange-500 rounded-full flex items-center justify-center shadow-lg">
            <XCircle className="w-14 h-14 text-white" strokeWidth={2.5} />
          </div>
        </div>

        {/* Title */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Payment {errorMessage.toLowerCase().includes('cancel') ? 'Canceled' : 'Failed'}
          </h1>
          <p className="text-gray-600">
            {canClose
              ? 'This window will close automatically...'
              : 'Your invoice subscription was not activated'
            }
          </p>
        </div>

        {/* Error Message */}
        <Alert variant="destructive" className="text-left">
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>

        {/* Action Buttons */}
        {!canClose && (
          <div className="space-y-3 pt-4">
            <Button onClick={handleRetry} className="w-full">
              {window.opener ? 'Try Again' : 'Return to Invoices'}
            </Button>

            {window.opener && (
              <Button
                onClick={() => window.close()}
                variant="outline"
                className="w-full"
              >
                Close This Tab
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
